import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useMutation } from "react-query";
import { DisplayQuote } from "../types/rate-analysis";
import { getCurrentStateAbbreviation } from "../utils/stateUtils";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

interface SpeakToProfessionalModalProps {
  isOpen: boolean;
  onClose: () => void;
  question?: string;
  quotes?: DisplayQuote[];
  selectedQuote?: DisplayQuote | null;
  showDropdown?: boolean;
  isUpdateRequest?: boolean;
  quoteRequestId?: string;
}

const emailDomain = import.meta.env.VITE_EMAIL_DOMAIN || "";

const SpeakToProfessionalModal: React.FC<SpeakToProfessionalModalProps> = ({
  isOpen,
  onClose,
  question,
  quotes = [],
  selectedQuote = null,
  showDropdown = false,
  isUpdateRequest = false,
  quoteRequestId,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    question: isUpdateRequest
      ? "Someone wants an updated quote request"
      : question || "",
    preferredContact: "email" as "email" | "phone" | "text",
  });

  // Add state for dropdown selection
  const [dropdownQuote, setDropdownQuote] = useState<DisplayQuote | null>(
    selectedQuote,
  );

  useEffect(() => {
    if (selectedQuote) {
      setDropdownQuote(selectedQuote);
    }
  }, [selectedQuote]);

  // Update default message when dropdownQuote changes
  useEffect(() => {
    if (isUpdateRequest) {
      // Don't override the predefined message for update requests
      return;
    }

    if (dropdownQuote) {
      setFormData((prev) => ({
        ...prev,
        question: `I am interested in the ${dropdownQuote.rate}% rate with ${
          dropdownQuote.lenderFees < 0
            ? `a credit of $${Math.abs(
                dropdownQuote.lenderFees,
              ).toLocaleString()} in lender fees`
            : `$${dropdownQuote.lenderFees.toLocaleString()} in lender fees`
        }.`,
      }));
    } else if (question) {
      setFormData((prev) => ({ ...prev, question }));
    }
  }, [dropdownQuote, question, isUpdateRequest]);

  const [errors, setErrors] = useState<Partial<typeof formData>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const submitMutation = useMutation(
    async (data: typeof formData) => {
      const response = await fetch(`${API_BASE_URL}/contact-requests/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email || null,
          phone: data.phone || null,
          question: data.question,
          preferredContact: data.preferredContact,
          state: getCurrentStateAbbreviation(),
          quoteRequestId: quoteRequestId || null,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      return response.json();
    },
    {
      onSuccess: () => {
        setIsSubmitted(true);
      },
      onError: (error) => {
        console.error("Error submitting form:", error);
        setErrors({
          question: "Failed to submit form. Please try again.",
        });
      },
    },
  );

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field if it exists
    if (errors[name as keyof typeof formData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Partial<typeof formData> = {};

    if (!formData.name) {
      newErrors.name = "Name is required";
    }

    if (!formData.email && !formData.phone) {
      newErrors.email = "Please provide either email or phone number";
      newErrors.phone = "Please provide either email or phone number";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (formData.phone && !/^\+?[\d\s-]{10,}$/.test(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!isUpdateRequest && !formData.question) {
      newErrors.question = "Please enter your question";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    submitMutation.mutate(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 animate-fade-in">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-heading font-semibold text-gray-900">
            {isUpdateRequest
              ? "Request Updated Rates"
              : "Speak to a Mortgage Professional"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <X size={24} />
          </button>
        </div>
        {showDropdown && quotes.length > 0 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select a quote
            </label>
            <select
              className="input-field w-full"
              value={
                dropdownQuote
                  ? `${dropdownQuote.rate}|${dropdownQuote.lenderFees}`
                  : ""
              }
              onChange={(e) => {
                const [rate, lenderFees] = e.target.value
                  .split("|")
                  .map(Number);
                const found = quotes.find(
                  (q) => q.rate === rate && q.lenderFees === lenderFees,
                );
                setDropdownQuote(found || null);
              }}
            >
              <option value="">Select...</option>
              {quotes.map((q, i) => (
                <option key={i} value={`${q.rate}|${q.lenderFees}`}>
                  {q.rate}% rate,{" "}
                  {q.lenderFees < 0
                    ? `$${Math.abs(
                        q.lenderFees,
                      ).toLocaleString()} lender credit`
                    : `$${q.lenderFees.toLocaleString()} lender fees`}
                </option>
              ))}
            </select>
          </div>
        )}
        {isSubmitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-success"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Thank You!
            </h3>
            <p className="text-gray-600">
              We've received your request and are looking forward to helping
              with your mortgage.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`input-field w-full ${
                  errors.name ? "border-error ring-1 ring-error" : ""
                }`}
                placeholder="Your name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-error">{errors.name}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`input-field w-full ${
                  errors.email ? "border-error ring-1 ring-error" : ""
                }`}
                placeholder="your@email.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-error">{errors.email}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`input-field w-full ${
                  errors.phone ? "border-error ring-1 ring-error" : ""
                }`}
                placeholder="(555) 555-5555"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-error">{errors.phone}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="preferredContact"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Preferred Method of Communication *
              </label>
              <select
                id="preferredContact"
                name="preferredContact"
                value={formData.preferredContact}
                onChange={handleChange}
                className="input-field w-full"
              >
                <option value="email">Email</option>
                <option value="phone">Phone Call</option>
                <option value="text">Text Message</option>
              </select>
            </div>

            {!isUpdateRequest && (
              <div>
                <label
                  htmlFor="question"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Message *
                </label>
                <textarea
                  id="question"
                  name="question"
                  value={formData.question}
                  onChange={handleChange}
                  rows={4}
                  className={`input-field w-full ${
                    errors.question ? "border-error ring-1 ring-error" : ""
                  }`}
                  placeholder="What is the best rate you can get me?"
                />
                <p className="text-sm text-gray-500">
                  You can also text or call us at 801-477-4216 or email us at{" "}
                  <a
                    href={`mailto:hey@${emailDomain}`}
                    className="text-primary"
                  >
                    {`hey@${emailDomain}`}
                  </a>
                </p>
                {errors.question && (
                  <p className="mt-1 text-sm text-error">{errors.question}</p>
                )}
              </div>
            )}

            <div className="pt-4">
              <button
                type="submit"
                disabled={submitMutation.isLoading}
                className="w-full btn-primary"
              >
                {submitMutation.isLoading
                  ? "Submitting..."
                  : isUpdateRequest
                    ? "Request Updated Rates"
                    : "Submit"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SpeakToProfessionalModal;
